<script lang="ts">
    import type {LocalizeFunction} from '../../../svelte/types';
    import {getContext} from 'svelte';
    import {LOCALIZE_KEY} from '../../../svelte/utils';
    import {range} from '../../../shared/utils/array-utils.fp';
    import type {HierarchyTree, HierarchyTreeNode, UUID} from '../../../typings/portaro.be.types';

    type ID = UUID | number | string;

    export let hierarchyTrees: HierarchyTree<ID, HierarchyTreeNode<ID>>[];
    export let numberOfInitiallyDisplayedRows = 6;

    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);

    let displayAllRows = false;

    function getAllLevelsOfHierarchyTree(hierarchyTree: HierarchyTree<ID, HierarchyTreeNode<ID>>): number[] {
        const levels = hierarchyTree.nodes.map((node) => node.level);
        return range(1, Math.max(...levels)); // level numbering starts at 1
    }

    function getNodesOnLevel(hierarchyTree: HierarchyTree<ID, HierarchyTreeNode<ID>>, level: number): HierarchyTreeNode<ID>[] {
        return hierarchyTree.nodes.filter((node) => node.level === level);
    }

    function getNodesOnLesserLevels(hierarchyTree: HierarchyTree<ID, HierarchyTreeNode<ID>>, level: number) {
        return hierarchyTree.nodes.filter((node) => node.level < level);
    }

    function getNodesOfPreviousHierarchyTrees(hierarchyTreeIndex: number): HierarchyTreeNode<ID>[] {
        return hierarchyTrees.slice(0, hierarchyTreeIndex).flatMap((hierarchy) => hierarchy.nodes)
    }

    function getCurrentRowIndex(currentNodeIndex: number, currentLevel: number,  currentHierarchyTree: HierarchyTree<ID, HierarchyTreeNode<ID>>, currentHierarchyTreeIndex: number) {
        return currentNodeIndex + getNodesOnLesserLevels(currentHierarchyTree, currentLevel).length + getNodesOfPreviousHierarchyTrees(currentHierarchyTreeIndex).length
    }

    function getOverallNodeCount(): number {
        return getNodesOfPreviousHierarchyTrees(hierarchyTrees.length).length; // get nodes from all hierarchyTrees [0..length-1]
    }
</script>

<template>
    <div class="hierarchy-container">
        {#each hierarchyTrees as hierarchyTree, hierarchyTreeIndex}
            {#each getAllLevelsOfHierarchyTree(hierarchyTree) as level (level)}
                <div class="level-block"  style="--left-margin-size: {level - 1}">
                    {#each getNodesOnLevel(hierarchyTree, level) as node, nodeIndex (node.id)}
                        <div class:hidden={!displayAllRows && getCurrentRowIndex(nodeIndex, level, hierarchyTree, hierarchyTreeIndex) >= numberOfInitiallyDisplayedRows}>
                            <slot {node}></slot>
                        </div>
                    {/each}
                </div>
            {/each}
        {/each}
        {#if getOverallNodeCount() > numberOfInitiallyDisplayedRows}
            <div class="show-all-or-less-button">
                {#if !displayAllRows}
                    <span>...</span>
                    <button type="button" class="btn btn-default btn-block btn-xs" on:click={() => displayAllRows = true}>
                        {localize(/* @kp-localization commons.ZobrazitVse */ 'commons.ZobrazitVse')}
                    </button>
                {:else}
                    <button type="button" class="btn btn-default btn-block btn-xs show-less-button" on:click={() => displayAllRows = false}>
                        {localize(/* @kp-localization commons.ShowLess */ 'commons.ShowLess')}
                    </button>
                {/if}
            </div>
        {/if}
    </div>
</template>

<style lang="less">
  .hierarchy-container {
    overflow: auto;
  }

  .level-block {
    margin-left: calc(10px * var(--left-margin-size))
  }

  .show-all-or-less-button {
    display: flex;
    flex-direction: column;
    align-items: center;

    .show-less-button {
      margin-top: 1em
    }
  }
</style>